<div class="analytics-page">
  <div class="page-header">
    <div>
      <h2>Analytics</h2>
      <p class="subtitle" *ngIf="data">
        {{ rangeLabel }}. Generated {{ data.generatedAt | date: 'medium' }}.
      </p>
    </div>

    <button
      mat-stroked-button
      class="export-button"
      *ngIf="data && !loading"
      [class.selected]="showExport"
      (click)="toggleExport()"
    >
      Export a PDF
    </button>
  </div>

  <div class="bot-bar" *ngIf="data && !loading">
    <mat-checkbox color="primary" [checked]="hideAutomated" (change)="toggleAutomated()">
      Hide automated traffic
    </mat-checkbox>
    <button type="button" class="explain" (click)="toggleFilterDetail()">
      How this works
      <span class="arrow">{{ showFilterDetail ? '&#9650;' : '&#9660;' }}</span>
    </button>
  </div>

  <mat-card class="filter-detail" *ngIf="showFilterDetail && data && !loading">
    <h3>How automated traffic is found</h3>
    <p>
      Not everything that reaches MyLink is a person. Automated programs, often
      called bots, load the site, take a copy, and leave. They show up in the
      numbers as visitors, which can make MyLink look busier than it is.
    </p>
    <p>
      They are easy to spot. They arrive in bulk from one place, on one kind of
      device, and spend almost no time on the page. A real visitor spends
      minutes. These spend less than a second.
    </p>
    <p>
      A group is left out when at least 50 visitors come from the same place on
      the same kind of device, and they both barely use MyLink and spend under
      30 seconds each. Both have to be true. Some genuine app users look
      inactive in the data because the app did not report properly, and the
      time they spent is what keeps them in.
    </p>
    <p>
      Two limits. A real person in the same place on the same kind of device is
      left out along with the programs. And a program that learned to linger on
      the page would pass as a person.
    </p>

    <h4 *ngIf="excludedPlaces.length">
      Left out of every number on this page, for {{ rangeLabel | lowercase }}:
    </h4>
    <table class="data-table" *ngIf="excludedPlaces.length">
      <tr>
        <th>Place</th>
        <th>Device</th>
        <th class="number">People</th>
        <th class="number">Engaged visits</th>
        <th class="number">Seconds each</th>
      </tr>
      <tr *ngFor="let group of excludedPlaces">
        <td>{{ placeLabel(group) }}</td>
        <td>{{ group.operatingSystem }}</td>
        <td class="number">{{ group.users | number }}</td>
        <td class="number">{{ group.engagedVisits | number }}</td>
        <td class="number quiet">{{ group.secondsPerUser | number: '1.2-2' }}</td>
      </tr>
    </table>
    <p class="card-note" *ngIf="!excludedPlaces.length">
      Nothing met the test in this period.
    </p>

  </mat-card>

  <div class="notice" *ngIf="errorMessage">{{ errorMessage }}</div>

  <div class="loading" *ngIf="loading">
    <mat-progress-spinner mode="indeterminate" diameter="48"></mat-progress-spinner>
    <p>Reading Google Analytics. This takes a few seconds.</p>
  </div>

  <div class="modal-backdrop" *ngIf="showExport && data && !loading" (click)="toggleExport()">
    <div class="modal-holder">
      <mat-card class="export-panel" (click)="$event.stopPropagation()">
    <h3>What should the file contain?</h3>

    <div class="export-groups">
      <div>
        <h4>
          Overview
          <button type="button" class="link" (click)="chooseAll('overview', true)">all</button>
          <button type="button" class="link" (click)="chooseAll('overview', false)">none</button>
        </h4>
        <div class="export-choice" *ngFor="let block of exportBlocks">
          <mat-checkbox
            color="primary"
            *ngIf="block.tab === 'overview'"
            [(ngModel)]="chosen[block.id]"
          >
            {{ block.title }}
          </mat-checkbox>
        </div>
      </div>

      <div>
        <h4>
          Compare
          <button type="button" class="link" (click)="chooseAll('compare', true)">all</button>
          <button type="button" class="link" (click)="chooseAll('compare', false)">none</button>
        </h4>
        <div class="export-choice" *ngFor="let block of exportBlocks">
          <mat-checkbox
            color="primary"
            *ngIf="block.tab === 'compare'"
            [(ngModel)]="chosen[block.id]"
          >
            {{ block.title }}
          </mat-checkbox>
        </div>
      </div>
    </div>

    <div class="export-actions">
      <button mat-stroked-button (click)="exportPdf()" [disabled]="!chosenCount || exporting">
        {{ exporting ? 'Building the file...' : 'Export ' + chosenCount + ' section' + (chosenCount === 1 ? '' : 's') }}
      </button>
      <span class="card-note" *ngIf="exporting">
        Switching tabs to collect the charts. This takes a moment.
      </span>
    </div>
    <div class="export-actions">
          <button mat-stroked-button (click)="toggleExport()">Cancel</button>
        </div>
      </mat-card>
    </div>
  </div>

  <div class="tab-bar">
    <button type="button" [class.active]="tab === 'overview'" (click)="setTab('overview')">
      Overview
    </button>
    <button type="button" [class.active]="tab === 'compare'" (click)="setTab('compare')">
      Compare
    </button>
  </div>

  <div class="tab-panel" *ngIf="tab === 'overview'">
    <div class="controls">
      <button
        mat-stroked-button
        *ngFor="let item of ranges"
        [class.selected]="range === item.key"
        (click)="setRange(item.key)"
      >
        {{ item.label }}
      </button>

      <button
        mat-stroked-button
        [class.selected]="range === 'custom'"
        (click)="openCustom()"
      >
        Custom range
      </button>

      <div class="custom-popup" *ngIf="showCustom">
        <mat-form-field appearance="standard">
          <mat-label>From</mat-label>
          <input matInput [matDatepicker]="fromPicker" [(ngModel)]="customStart" />
          <mat-datepicker-toggle matSuffix [for]="fromPicker"></mat-datepicker-toggle>
          <mat-datepicker #fromPicker></mat-datepicker>
        </mat-form-field>

        <mat-form-field appearance="standard">
          <mat-label>To</mat-label>
          <input matInput [matDatepicker]="toPicker" [(ngModel)]="customEnd" />
          <mat-datepicker-toggle matSuffix [for]="toPicker"></mat-datepicker-toggle>
          <mat-datepicker #toPicker></mat-datepicker>
        </mat-form-field>

        <div class="popup-actions">
          <button
            mat-stroked-button
            (click)="applyCustom()"
            [disabled]="!customStart || !customEnd || customStart > customEnd"
          >
            Show these dates
          </button>
          <button type="button" class="clear-dates" (click)="clearCustom()">
            Clear
          </button>
        </div>
      </div>
  </div>

  <div class="cards" *ngIf="data && !loading">

    <mat-card class="card wide">
      <h3>How many people use MyLink?</h3>
      <p class="card-note">
        <span *ngIf="usageByMonth">
          Each person counts once per month, however many times they visit.
        </span>
        <span *ngIf="!usageByMonth">
          Each person counts once per day, however many times they visit.
        </span>
      </p>
      <div class="chart-holder tall" *ngIf="rows('usageOverTime').length">
        <canvas #chartCanvas id="usageOverTime"></canvas>
      </div>
      <p class="empty" *ngIf="!rows('usageOverTime').length">
        No visits in this period.
      </p>
    </mat-card>

    <div class="card-pair">
    <mat-card class="card">
        <h3>How long do people stay?</h3>
        <p class="empty" *ngIf="!engagement">No visits in this period.</p>
        <div class="tiles" *ngIf="engagement">
          <div>
            <p class="big-number">{{ (engagement.metrics[0] / 60) | number: '1.0-1' }}</p>
            <p class="card-note">minutes, average visit</p>
          </div>
          <div>
            <p class="big-number">{{ (engagement.metrics[1] * 100) | number: '1.0-0' }}%</p>
            <p class="card-note">of visits lasted over 10 seconds, or reached a second page</p>
          </div>
        </div>
      </mat-card>
    <mat-card class="card">
        <h3>Did people find something to act on?</h3>
        <p class="card-note">
          Program pages opened, and what people did next. Calls and directions
          come from the phone apps only. On the website the phone number and
          address are plain text, so there is nothing to tap. This card counts
          from September 2026, when the tracking was released, and cannot be
          filled in for earlier dates.
        </p>
        <div class="chart-holder" *ngIf="rows('programEngagement').length">
          <canvas #chartCanvas id="programEngagement"></canvas>
        </div>
        <p class="empty" *ngIf="!rows('programEngagement').length">
          No data yet.
        </p>
      </mat-card>
    </div>

    <mat-card class="card wide">
      <h3>Categories people picked</h3>
      <p class="card-note">
        Google Analytics reports a measurement only from the day it was
        registered. Category picks were registered in mid-2024, so nothing
        shows before then. The website stopped sending them in September
        2025, when the v2 rewrite dropped the events. The fix was released in
        September 2026, so dates in between count the phone app only. A
        category picked in another language counts under its translated name,
        so some categories appear here more than once.
      </p>
      <div class="chart-holder tall" *ngIf="rows('topCategories').length">
        <canvas #chartCanvas id="topCategories"></canvas>
      </div>
      <p class="empty" *ngIf="!rows('topCategories').length">
        Nobody picked a category in this period.
      </p>
      <div class="split-tables">
        <table class="data-table" *ngFor="let column of categoryColumns">
          <tr>
            <th>Category</th>
            <th class="number">Times picked</th>
          </tr>
          <tr *ngFor="let row of column">
            <td>{{ row.dimensions[0] }}</td>
            <td class="number">{{ row.metrics[0] | number }}</td>
          </tr>
        </table>
      </div>
    </mat-card>

    <mat-card class="card wide">
      <h3>What each city is looking for</h3>
      <p class="card-note">
        The top categories picked in each place, most active first.
        <strong>City here is where the visitor's device is</strong>, which
        Google works out from the network. It is not the MyLNK location they
        chose to browse. Somebody in Kansas City who reads Lincoln listings
        counts under Kansas City. The limits listed on the categories card
        above apply here too.
      </p>
      <div class="city-grid">
        <div class="city" *ngFor="let group of cityCategories">
          <h4>{{ group.city }}</h4>
          <table class="data-table">
            <tr *ngFor="let row of group.rows">
              <td>{{ row.dimensions[2] }}</td>
              <td class="number">{{ row.metrics[0] | number }}</td>
            </tr>
          </table>
        </div>
      </div>
      <p class="empty" *ngIf="!cityCategories.length">
        No category data for this period.
      </p>
    </mat-card>

    <mat-card class="card">
      <h3>What people searched for</h3>
      <table class="data-table">
        <tr>
          <th>Search</th>
          <th class="number">Times</th>
        </tr>
        <tr *ngFor="let row of rows('topSearches')">
          <td class="term">{{ row.dimensions[0] }}</td>
          <td class="number">{{ row.metrics[0] | number }}</td>
        </tr>
      </table>
      <p class="empty" *ngIf="!rows('topSearches').length">No searches in this period.</p>
    </mat-card>

    <mat-card class="card">
      <h3>Searches that found nothing</h3>
      <p class="big-number">{{ emptySearchCount | number }}</p>
      <p class="card-note" *ngIf="searchTotal">
        Out of {{ searchTotal | number }} searches that reported a result count.
        Not every version of MyLink reports one, so this is a sample rather than
        every search.
      </p>
      <p class="empty" *ngIf="!searchTotal">
        Nothing yet. This starts once the corrected tracking is released.
      </p>
    </mat-card>

    <mat-card class="card">
      <h3>Where people are</h3>
      <table class="data-table">
        <tr>
          <th>Place</th>
          <th class="number">People</th>
        </tr>
        <tr *ngFor="let row of places">
          <td>{{ row.dimensions[1] }}, {{ row.dimensions[0] }}</td>
          <td class="number">{{ row.metrics[0] | number }}</td>
        </tr>
      </table>
      <p class="empty" *ngIf="!places.length">No visits in this period.</p>
    </mat-card>

    <mat-card class="card">
      <h3>Language</h3>
      <div class="table-scroll">
        <table class="data-table">
          <tr>
            <th>Language</th>
            <th class="number">Device set to</th>
            <th class="number">Picked in MyLink</th>
            <th class="number">Average visit</th>
          </tr>
          <tr *ngFor="let row of languageRows">
            <td>{{ row.name }}</td>
            <td class="number">{{ row.people ? (row.people | number) : '-' }}</td>
            <td class="number">{{ row.picked ? (row.picked | number) : '-' }}</td>
            <td class="number quiet">
              {{ row.people ? ((row.seconds / 60) | number: '1.0-1') + ' min' : '-' }}
            </td>
          </tr>
        </table>
      </div>
      <p class="empty" *ngIf="!languageRows.length">No visits in this period.</p>
      <p class="card-note">
        <strong>Device set to</strong> is the language the visitor's phone or
        browser runs in. Visitors GA could not match to a language are grouped
        as "(other)".
        <strong>Picked in MyLink</strong> counts people who chose a language in
        the app. Somebody who never opens the language menu is not counted
        there. The website stopped sending this in September 2025, when the
        v2 rewrite dropped the event. The fix was released in September 2026,
        so dates in between count the phone app only.
      </p>
    </mat-card>

      </div>
  </div>

  <div class="tab-panel" *ngIf="tab === 'compare'">
      <div class="tab-body" *ngIf="data && !loading">
        <mat-card class="card wide">
      <h3>Compare two periods</h3>
      <div class="compare-dates">
        <span class="compare-label">Compare</span>
        <mat-form-field appearance="standard">
          <mat-label>From</mat-label>
          <input matInput [matDatepicker]="c1" [(ngModel)]="compareEarlierStart" />
          <mat-datepicker-toggle matSuffix [for]="c1"></mat-datepicker-toggle>
          <mat-datepicker #c1></mat-datepicker>
        </mat-form-field>
        <mat-form-field appearance="standard">
          <mat-label>To</mat-label>
          <input matInput [matDatepicker]="c2" [(ngModel)]="compareEarlierEnd" />
          <mat-datepicker-toggle matSuffix [for]="c2"></mat-datepicker-toggle>
          <mat-datepicker #c2></mat-datepicker>
        </mat-form-field>

        <span class="compare-label">with</span>
        <mat-form-field appearance="standard">
          <mat-label>From</mat-label>
          <input matInput [matDatepicker]="c3" [(ngModel)]="compareLaterStart" />
          <mat-datepicker-toggle matSuffix [for]="c3"></mat-datepicker-toggle>
          <mat-datepicker #c3></mat-datepicker>
        </mat-form-field>
        <mat-form-field appearance="standard">
          <mat-label>To</mat-label>
          <input matInput [matDatepicker]="c4" [(ngModel)]="compareLaterEnd" />
          <mat-datepicker-toggle matSuffix [for]="c4"></mat-datepicker-toggle>
          <mat-datepicker #c4></mat-datepicker>
        </mat-form-field>

        <button mat-stroked-button (click)="applyComparison()" [disabled]="!comparisonReady">
          Compare
        </button>
      </div>

      <p class="card-note problem" *ngIf="comparisonProblem">
        {{ comparisonProblem }}
      </p>

    </mat-card>

    <mat-card class="card wide">
      <h3>People</h3>

      <p class="empty" *ngIf="!compareTotals.length">
        No visits in one of these periods.
      </p>

      <div class="chart-holder tall" *ngIf="compareTotals.length">
        <canvas #compareCanvas id="compare-visitors"></canvas>
      </div>

      <table class="data-table" *ngIf="compareTotals.length">
        <tr>
          <th>Measure</th>
          <th class="number">
            {{ data.comparison.secondStart }} to {{ data.comparison.secondEnd }}
          </th>
          <th class="number">
            {{ data.comparison.firstStart }} to {{ data.comparison.firstEnd }}
          </th>
          <th class="number">Change</th>
        </tr>
        <tr *ngFor="let row of compareTotals">
          <td>
            <span class="measure-name">{{ row.measure }}</span>
            <span class="measure-explain">{{ row.explain }}</span>
          </td>
          <td class="number">{{ row.earlierText }}</td>
          <td class="number">{{ row.laterText }}</td>
          <td class="number change" [class.up]="row.change > 0" [class.down]="row.change < 0">
            <span *ngIf="row.change !== null">
              {{ row.change > 0 ? '+' : '' }}{{ row.change | number: '1.0-0' }}%
            </span>
            <span *ngIf="row.change === null" class="card-note">new</span>
          </td>
        </tr>
      </table>

    </mat-card>

    <mat-card class="card wide" *ngFor="let section of compareSections">
      <h3>{{ section.title }}</h3>

      <p class="card-note" *ngIf="section.note">{{ section.note }}</p>

      <div class="chart-holder tall" *ngIf="compareTop[section.key]?.length">
        <canvas #compareCanvas [id]="'compare-' + section.key"></canvas>
      </div>

      <!-- The legend is painted inside the canvas, which the keyboard cannot
           reach. These buttons do the same job and appear when focused. -->
      <div class="key-toggles" *ngIf="compareTop[section.key]?.length">
        <button
          type="button"
          class="key-toggle"
          *ngFor="let name of compareTop[section.key]"
          [attr.aria-pressed]="!isItemHidden(section.key, name)"
          (click)="toggleItemByName(section.key, name)"
        >
          {{ isItemHidden(section.key, name) ? 'Show' : 'Hide' }} {{ name }}
        </button>
      </div>

      <p class="card-note" *ngIf="compareTop[section.key]?.length">
        Solid lines are {{ periodLabel(data.comparison.firstStart, data.comparison.firstEnd) }}.
        Dashed lines are {{ periodLabel(data.comparison.secondStart, data.comparison.secondEnd) }}.
        Click a name above the chart to show or hide it.
      </p>

      <table class="data-table" *ngIf="shares[section.key]?.length">
        <tr>
          <th>{{ section.title }}</th>
          <th class="number">
            {{ data.comparison.secondStart }} to {{ data.comparison.secondEnd }}
          </th>
          <th class="number">
            {{ data.comparison.firstStart }} to {{ data.comparison.firstEnd }}
          </th>
          <th class="number">Change</th>
        </tr>
        <tr *ngFor="let row of shares[section.key]">
          <td>{{ row.name }}</td>
          <td class="number">{{ row.second | number: '1.0-1' }}%</td>
          <td class="number">{{ row.first | number: '1.0-1' }}%</td>
          <td class="number change" [class.up]="row.change > 0" [class.down]="row.change < 0">
            {{ row.change > 0 ? '+' : '' }}{{ row.change | number: '1.0-1' }} pts
          </td>
        </tr>
      </table>

      <p class="card-note" *ngIf="shares[section.key]?.length">
        The chart shows counts per month for the top six, across both periods.
        The table shows each one's share of all {{ section.unit }} in each
        period, ordered by how much the share moved, risers first.
      </p>

      <p class="empty" *ngIf="!shares[section.key]?.length">
        No data in one of these periods.
      </p>
        </mat-card>

      </div>
  </div>
</div>
